import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid2,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  useTheme,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Block as BlockIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Info as InfoIcon,
  Inventory2 as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";
import { observer } from "mobx-react";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { StockIssueRequest } from "../../../models/StockIssueRequest/StockIssueRequest";
import { StockIssueRequestStore } from "../../../stores/StockIssueRequestStore";
import CustomTypography from "../../components/common/form/CustomTypography";
import AppButton from "../../components/common/buttons/AppButton";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import ConfirmationPopup from "../../components/common/pop-up/ConfirmationPopup";
import AppTableHeaderCell from "../../components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../components/common/pages/AppTableRowCell";
import { formatDate } from "../../components/common/functions/formatDate";

const getStatusChip = (req: StockIssueRequest) => {
  if (req.cancelled === 1)
    return (
      <Chip
        label="Cancelled"
        sx={{ backgroundColor: "#FEF3C7", color: "#92400E", fontWeight: 600 }}
      />
    );
  if (req.rejected === 1)
    return (
      <Chip
        label="Rejected"
        sx={{ backgroundColor: "#FEE2E2", color: "#991B1B", fontWeight: 600 }}
      />
    );
  if (req.auto_approved === 1)
    return (
      <Chip
        label="Auto Approved"
        sx={{ backgroundColor: "#CCFBF1", color: "#0F766E", fontWeight: 600 }}
      />
    );
  if (req.approved === 1)
    return (
      <Chip
        label="Approved"
        sx={{ backgroundColor: "#DCFCE7", color: "#166534", fontWeight: 600 }}
      />
    );
  return (
    <Chip
      label="Pending"
      sx={{ backgroundColor: "#EFF6FF", color: "#1D4ED8", fontWeight: 600 }}
    />
  );
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box>
    <CustomTypography
      variant="body2"
      color="text.secondary"
      fontWeight={500}
      sx={{ mb: 0.5 }}
    >
      {label}
    </CustomTypography>
    <CustomTypography variant="body1" fontWeight={600} color="text.primary">
      {value !== null && value !== undefined && value !== "" ? value : "—"}
    </CustomTypography>
  </Box>
);

const StockIssueRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme<Theme>();
  const { userStore } = useStores();
  const { showSuccess, showError } = useNotification();

  const store = useRef(new StockIssueRequestStore()).current;
  const requestId = Number(id);

  const [isLoading, setIsLoading] = useState(false);
  const [approvePopupOpen, setApprovePopupOpen] = useState(false);
  const [rejectPopupOpen, setRejectPopupOpen] = useState(false);

  const hasViewPermission = userStore.hasPermission([
    EmployeeRoles.VIEW_STOCK_ISSUE_REQUESTS,
  ]);
  const hasApprovePermission = userStore.hasPermission([
    EmployeeRoles.APPROVE_STOCK_ISSUE_REQUESTS,
  ]);

  useEffect(() => {
    if (!hasViewPermission || !requestId) return;
    setIsLoading(true);
    store
      .getStockIssueRequestById(requestId)
      .catch((err: any) =>
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        ),
      )
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const req = store.currentRequest;
  const isPending = req
    ? !req.approved && !req.auto_approved && !req.rejected && !req.cancelled
    : false;

  const handleApproveConfirm = (_id: number) => {
    store.api
      .approveStockIssueRequest(requestId)
      .then(() => {
        setApprovePopupOpen(false);
        return store.getStockIssueRequestById(requestId);
      })
      .catch((err: any) =>
        showError(
          "Error",
          err?.response?.data?.message ||
            "Failed to approve. Please try again.",
        ),
      );
  };

  const handleRejectConfirm = (_id: number, reason?: string) => {
    if (!reason?.trim()) {
      showError("Error", "Please provide a rejection reason.");
      return;
    }
    store.api
      .rejectStockIssueRequest(requestId, reason)
      .then(() => {
        setRejectPopupOpen(false);
        return store.getStockIssueRequestById(requestId);
      })
      .catch((err: any) =>
        showError(
          "Error",
          err?.response?.data?.message || "Failed to reject. Please try again.",
        ),
      );
  };

  const productGroups = req
    ? req.products.map((product) => ({
        product,
        mappings: req.order_products_mapping.filter(
          (m) => m.product_id === product.product_id,
        ),
      }))
    : [];

  const orphanMappings = req
    ? req.order_products_mapping.filter(
        (m) => !req.products.some((p) => p.product_id === m.product_id),
      )
    : [];

  const statusColor =
    req?.cancelled === 1
      ? "#92400E"
      : req?.rejected === 1
        ? "#DC2626"
        : req?.auto_approved === 1 || req?.approved === 1
          ? "#16A34A"
          : "#2563EB";

  const statusBg =
    req?.cancelled === 1
      ? "#FFFBEB"
      : req?.rejected === 1
        ? "#FFF1F2"
        : req?.auto_approved === 1 || req?.approved === 1
          ? "#F0FDF4"
          : "#EFF6FF";

  const StatusIcon =
    req?.cancelled === 1
      ? BlockIcon
      : req?.rejected === 1
        ? CancelIcon
        : req?.auto_approved === 1
          ? VerifiedIcon
          : req?.approved === 1
            ? CheckCircleIcon
            : HourglassEmptyIcon;

  const statusLabel =
    req?.cancelled === 1
      ? "Cancelled"
      : req?.rejected === 1
        ? "Rejected"
        : req?.auto_approved === 1
          ? "Auto Approved"
          : req?.approved === 1
            ? "Approved"
            : "Pending Approval";

  const getStatusIcon = (approval: number, rejectionDate: string | null) => {
    if (rejectionDate) {
      return <CancelIcon sx={{ color: "error.main", fontSize: 24 }} />;
    }
    if (approval === 1) {
      return <CheckCircleIcon sx={{ color: "success.main", fontSize: 24 }} />;
    }
    return <PendingIcon sx={{ color: "warning.main", fontSize: 24 }} />;
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {/* No permission */}
      {!hasViewPermission && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
              p: 2,
              backgroundColor: theme.palette.primary.main + "08",
              borderRadius: 2,
              border: `2px solid ${theme.palette.primary.main}20`,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: theme.palette.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <InventoryIcon sx={{ color: "white", fontSize: 20 }} />
            </Box>
            <Box>
              <CustomTypography
                variant="heading5"
                fontWeight={700}
                color="primary.main"
              >
                Stock Issue Request Details
              </CustomTypography>
              <CustomTypography variant="body2" color="text.secondary">
                View stock issue request details
              </CustomTypography>
            </Box>
          </Box>
          <PermissionBlockLabel />
        </>
      )}

      {hasViewPermission && (
        <>
          {/* Back button */}
          <Box sx={{ mb: 2 }}>
            <AppButton
              label="Go back"
              variant="transparentPrimary"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            />
          </Box>

          {/* Loading */}
          {isLoading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "50vh",
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {/* Not found */}
          {!isLoading && !req && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "40vh",
              }}
            >
              <CustomTypography variant="heading5" color="text.secondary">
                Request not found.
              </CustomTypography>
            </Box>
          )}

          {/* Main content */}
          {!isLoading && req && (
            <>
              {/* ── Section 1: Details ── */}
              <Box sx={{ mb: 4 }}>
                {/* Section banner */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 3,
                    p: 2,
                    backgroundColor: theme.palette.primary.main + "08",
                    borderRadius: 2,
                    border: `2px solid ${theme.palette.primary.main}20`,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.primary.main,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <InventoryIcon sx={{ color: "white", fontSize: 20 }} />
                  </Box>
                  <Box>
                    <CustomTypography
                      variant="heading5"
                      fontWeight={700}
                      color="primary.main"
                    >
                      Stock Issue Request Details
                    </CustomTypography>
                    <CustomTypography variant="body2" color="text.secondary">
                      Basic information and approval status
                    </CustomTypography>
                  </Box>
                </Box>

                <Paper
                  sx={{
                    p: { xs: 2, sm: 3, md: 4 },
                    background: theme.palette.customColors?.white,
                    borderRadius: 3,
                    boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.08)",
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        minWidth: 0,
                      }}
                    >
                      <CustomTypography
                        variant="heading3"
                        fontWeight={700}
                        color="primary.main"
                      >
                        Stock Issue Request #{req.id}
                      </CustomTypography>
                      {req.warehouse_id && (
                        <Chip
                          label={req.warehouse_id}
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 600, height: 32 }}
                        />
                      )}
                      {getStatusChip(req)}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      {Boolean(req.approved) === false &&
                        req.rejected_by_admin_id === null && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <AppButton
                              label="Approve"
                              variant="primary"
                              onClick={() => setApprovePopupOpen(true)}
                              customStyles={{
                                minWidth: 100,
                                height: 36,
                              }}
                            />
                            <AppButton
                              label="Reject"
                              variant="secondary"
                              onClick={() => setRejectPopupOpen(true)}
                              customStyles={{
                                minWidth: 100,
                                height: 36,
                              }}
                            />
                          </Box>
                        )}
                    </Box>
                  </Box>

                  {/* Rejection banner */}
                  {req.rejected === 1 && (
                    <Box
                      sx={{
                        backgroundColor: theme.palette.error.main + "15",
                        border: `1px solid ${theme.palette.error.main}40`,
                        borderRadius: 2,
                        p: 2,
                        mb: 3,
                        borderLeft: `4px solid ${theme.palette.error.main}`,
                      }}
                    >
                      <CustomTypography
                        variant="body1"
                        color="error.main"
                        fontWeight={600}
                      >
                        Stock Issue Request Rejected
                      </CustomTypography>
                      <CustomTypography
                        variant="body2"
                        color="error.main"
                        sx={{ mt: 0.75 }}
                      >
                        <strong>Rejected By:</strong> Admin #
                        {req.rejected_by_admin_name}
                        {req.rejection_reason && (
                          <>
                            <br />
                            <strong>Reason:</strong> {req.rejection_reason}
                          </>
                        )}
                      </CustomTypography>
                    </Box>
                  )}

                  {/* Cancellation banner */}
                  {req.cancelled === 1 && (
                    <Box
                      sx={{
                        backgroundColor: "#FFFBEB",
                        border: "1px solid #FDE68A",
                        borderRadius: 2,
                        p: 2,
                        mb: 3,
                        borderLeft: "4px solid #F59E0B",
                      }}
                    >
                      <CustomTypography
                        variant="body1"
                        fontWeight={600}
                        sx={{ color: "#92400E" }}
                      >
                        Stock Issue Request Cancelled
                      </CustomTypography>
                      <CustomTypography
                        variant="body2"
                        sx={{ mt: 0.75, color: "#92400E" }}
                      >
                        <strong>Cancelled By:</strong> Admin #
                        {req.cancelled_by_admin_name}
                        {req.cancellation_reason && (
                          <>
                            <br />
                            <strong>Reason:</strong> {req.cancellation_reason}
                          </>
                        )}
                      </CustomTypography>
                    </Box>
                  )}

                  {/* Details grid */}
                  <Grid2 container spacing={3}>
                    {/* Left card: Request Info */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <Card
                        sx={{
                          height: "100%",
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.12)",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              mb: 3,
                              pb: 2,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                backgroundColor:
                                  theme.palette.primary.main + "15",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <InfoIcon
                                sx={{ color: "primary.main", fontSize: 20 }}
                              />
                            </Box>
                            <CustomTypography
                              variant="heading6"
                              fontWeight={600}
                            >
                              Request Information
                            </CustomTypography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                              }}
                            >
                              <InfoRow
                                label="Warehouse"
                                value={req.warehouse_id}
                              />
                              <InfoRow
                                label="Raised By"
                                value={`${req.created_by_admin_name}`}
                              />
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                              }}
                            >
                              <InfoRow
                                label="Total Units"
                                value={String(
                                  req.order_products_mapping.reduce(
                                    (s, m) => s + (m.quantity ?? 0),
                                    0,
                                  ),
                                )}
                              />
                              <InfoRow
                                label="Created On"
                                value={formatDate(req.created_date)}
                              />
                              <InfoRow
                                label="Last Modified"
                                value={formatDate(req.modified_date)}
                              />
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid2>

                    {/* Right card: Approval Status */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <Card
                        sx={{
                          height: "100%",
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.12)",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              mb: 3,
                              pb: 2,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                backgroundColor:
                                  theme.palette.warning.main + "15",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <CheckCircleIcon
                                sx={{ color: "warning.main", fontSize: 20 }}
                              />
                            </Box>
                            <CustomTypography
                              variant="heading6"
                              fontWeight={600}
                            >
                              Approval Status
                            </CustomTypography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2.5,
                            }}
                          >
                            {/* Status pill */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: statusBg,
                              }}
                            >
                              <StatusIcon
                                sx={{
                                  color: statusColor,
                                  fontSize: 32,
                                  flexShrink: 0,
                                }}
                              />
                              <Box>
                                <CustomTypography
                                  variant="body2"
                                  color="text.secondary"
                                  fontWeight={500}
                                >
                                  Current Status
                                </CustomTypography>
                                <CustomTypography
                                  variant="body1"
                                  fontWeight={700}
                                  sx={{ color: statusColor }}
                                >
                                  {statusLabel}
                                </CustomTypography>
                              </Box>
                            </Box>

                            {/* Approved / Auto-approved details */}
                            {(req.approved === 1 || req.auto_approved === 1) &&
                              req.approved_by_admin_id && (
                                <Box>
                                  <CustomTypography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                    sx={{ mb: 0.5 }}
                                  >
                                    Approved By
                                  </CustomTypography>
                                  <CustomTypography
                                    variant="body1"
                                    fontWeight={600}
                                    color="success.main"
                                  >
                                    {req.approved_by_admin_name}
                                  </CustomTypography>
                                  {req.approval_date && (
                                    <CustomTypography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.5 }}
                                    >
                                      Approved on:{" "}
                                      {formatDate(req.approval_date)}
                                    </CustomTypography>
                                  )}
                                </Box>
                              )}

                            {/* Rejected details */}
                            {req.rejected === 1 && (
                              <>
                                {req.rejected_by_admin_id && (
                                  <Box>
                                    <CustomTypography
                                      variant="body2"
                                      color="text.secondary"
                                      fontWeight={500}
                                      sx={{ mb: 0.5 }}
                                    >
                                      Rejected By
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body1"
                                      fontWeight={600}
                                      color="error.main"
                                    >
                                      {req.rejected_by_admin_name}
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.5 }}
                                    >
                                      Rejected on:{" "}
                                      {formatDate(req.rejection_date)}
                                    </CustomTypography>
                                  </Box>
                                )}
                                {req.rejection_reason && (
                                  <Box>
                                    <CustomTypography
                                      variant="body2"
                                      color="text.secondary"
                                      fontWeight={500}
                                      sx={{ mb: 0.5 }}
                                    >
                                      Rejection Reason
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body2"
                                      fontWeight={500}
                                      color="error.main"
                                    >
                                      {req.rejection_reason}
                                    </CustomTypography>
                                  </Box>
                                )}
                              </>
                            )}

                            {/* Cancelled details */}
                            {req.cancelled === 1 && (
                              <>
                                {req.cancelled_by_admin_id && (
                                  <Box>
                                    <CustomTypography
                                      variant="body2"
                                      color="text.secondary"
                                      fontWeight={500}
                                      sx={{ mb: 0.5 }}
                                    >
                                      Cancelled By
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body1"
                                      fontWeight={600}
                                      sx={{ color: "#92400E" }}
                                    >
                                      {req.cancelled_by_admin_name}
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.5 }}
                                    >
                                      Cancelled on:{" "}
                                      {formatDate(req.cancellation_date)}
                                    </CustomTypography>
                                  </Box>
                                )}
                                {req.cancellation_reason && (
                                  <Box>
                                    <CustomTypography
                                      variant="body2"
                                      color="text.secondary"
                                      fontWeight={500}
                                      sx={{ mb: 0.5 }}
                                    >
                                      Cancellation Reason
                                    </CustomTypography>
                                    <CustomTypography
                                      variant="body2"
                                      fontWeight={500}
                                      sx={{ color: "#92400E" }}
                                    >
                                      {req.cancellation_reason}
                                    </CustomTypography>
                                  </Box>
                                )}
                              </>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid2>
                  </Grid2>
                </Paper>
              </Box>

              {/* Gradient divider */}
              <Box
                sx={{
                  height: 2,
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.success.main} 50%, ${theme.palette.warning.main} 100%)`,
                  borderRadius: 1,
                  mb: 4,
                }}
              />

              {/* ── Section 2: Products ── */}
              <Box sx={{ mb: 4 }}>
                {/* Section banner */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 3,
                    p: 2,
                    backgroundColor: theme.palette.success.main + "08",
                    borderRadius: 2,
                    border: `2px solid ${theme.palette.success.main}20`,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.success.main,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <ShoppingCartIcon sx={{ color: "white", fontSize: 20 }} />
                  </Box>
                  <Box>
                    <CustomTypography
                      variant="heading5"
                      fontWeight={700}
                      color="success.main"
                    >
                      Products
                    </CustomTypography>
                    <CustomTypography variant="body2" color="text.secondary">
                      {req.products.length} product
                      {req.products.length !== 1 ? "s" : ""} in this request
                    </CustomTypography>
                  </Box>
                </Box>

                {/* Empty state */}
                {productGroups.length === 0 && orphanMappings.length === 0 && (
                  <Card
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 3,
                      boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                    }}
                  >
                    <CardContent>
                      <Box sx={{ py: 5, textAlign: "center" }}>
                        <CustomTypography
                          variant="heading6"
                          color="text.secondary"
                        >
                          No products found for this request.
                        </CustomTypography>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Product cards */}
                {productGroups.map(({ product, mappings }, index) => (
                  <Card
                    key={product.id}
                    sx={{
                      mb: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 3,
                      boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: "0px 8px 32px 0px rgba(0, 0, 0, 0.12)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                      {/* Product header */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 3,
                          pb: 3,
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          flexWrap: "wrap",
                        }}
                      >
                        <CustomTypography
                          variant="heading5"
                          fontWeight={700}
                          color="primary.main"
                        >
                          {index + 1}.
                        </CustomTypography>
                        <CustomTypography
                          variant="heading5"
                          fontWeight={700}
                          color="primary.main"
                        >
                          {product.product_name}
                        </CustomTypography>
                        <Chip
                          label={`ID: ${product.product_id}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </Box>

                      {/* Stats boxes */}
                      <Grid2 container spacing={2} sx={{ mb: 4 }}>
                        {[
                          { label: "Rate", value: `₹${product.rate}` },
                          {
                            label: "GST",
                            value:
                              product.gst_percentage != null
                                ? `${product.gst_percentage}%`
                                : "N/A",
                          },
                          {
                            label: "Total Units",
                            value: String(
                              mappings.reduce(
                                (s, m) => s + (m.quantity ?? 0),
                                0,
                              ),
                            ),
                          },
                          {
                            label: "Orders",
                            value: String(
                              new Set(
                                mappings.map((m) => m.order_id).filter(Boolean),
                              ).size,
                            ),
                          },
                        ].map(({ label, value }) => (
                          <Grid2 key={label} size={{ xs: 6, sm: 3 }}>
                            <Box
                              sx={{
                                p: 2.5,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                                backgroundColor: theme.palette.background.paper,
                                transition: "all 0.2s ease-in-out",
                                minHeight: 80,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                "&:hover": {
                                  boxShadow:
                                    "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                  borderColor:
                                    theme.palette.primary.main + "40",
                                },
                              }}
                            >
                              <CustomTypography
                                variant="body2"
                                color="text.secondary"
                                fontWeight={500}
                              >
                                {label}
                              </CustomTypography>
                              <CustomTypography
                                variant="heading5"
                                fontWeight={700}
                              >
                                {value}
                              </CustomTypography>
                            </Box>
                          </Grid2>
                        ))}
                      </Grid2>

                      {/* Order Mappings sub-section */}
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: 2,
                            pb: 1.5,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          <CustomTypography variant="heading6" fontWeight={600}>
                            Order Mappings
                          </CustomTypography>
                          <Chip
                            label={mappings.length}
                            size="small"
                            sx={{ fontWeight: 600, fontSize: "0.72rem" }}
                          />
                        </Box>

                        {mappings.length === 0 ? (
                          <Box
                            sx={{
                              py: 3,
                              textAlign: "center",
                              backgroundColor:
                                theme.palette.customColors
                                  ?.grayBackGroundColor ||
                                theme.palette.action.hover,
                              borderRadius: 2,
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              color="text.secondary"
                            >
                              No order mappings for this product.
                            </CustomTypography>
                          </Box>
                        ) : (
                          <TableContainer
                            sx={{
                              borderRadius: 2,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Table size="medium">
                              <TableHead>
                                <TableRow
                                  sx={{
                                    backgroundColor:
                                      theme.palette.customColors
                                        ?.lightGreyColor,
                                    "& th": { py: 1, height: 40 },
                                  }}
                                >
                                  <AppTableHeaderCell
                                    sx={{
                                      textAlign: "center",
                                      fontSize: 12,
                                      minWidth: "50px",
                                    }}
                                    fontSize="12px"
                                  >
                                    #
                                  </AppTableHeaderCell>
                                  <AppTableHeaderCell
                                    sx={{
                                      textAlign: "center",
                                      fontSize: 12,
                                      minWidth: "110px",
                                    }}
                                    fontSize="12px"
                                  >
                                    Order ID
                                  </AppTableHeaderCell>
                                  <AppTableHeaderCell
                                    sx={{
                                      textAlign: "center",
                                      fontSize: 12,
                                      minWidth: "130px",
                                    }}
                                    fontSize="12px"
                                  >
                                    SKU
                                  </AppTableHeaderCell>
                                  <AppTableHeaderCell
                                    sx={{
                                      textAlign: "left",
                                      fontSize: 12,
                                      minWidth: "200px",
                                    }}
                                    fontSize="12px"
                                  >
                                    Description
                                  </AppTableHeaderCell>
                                  <AppTableHeaderCell
                                    sx={{
                                      textAlign: "center",
                                      fontSize: 12,
                                      minWidth: "90px",
                                    }}
                                    fontSize="12px"
                                  >
                                    Quantity
                                  </AppTableHeaderCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {mappings.map((mapping, idx) => (
                                  <TableRow
                                    key={mapping.id}
                                    hover
                                    sx={{
                                      "&:nth-of-type(odd)": {
                                        backgroundColor:
                                          theme.palette.background.paper,
                                      },
                                      "&:nth-of-type(even)": {
                                        backgroundColor:
                                          theme.palette.action.hover,
                                      },
                                      "& td": { fontSize: "12px" },
                                    }}
                                  >
                                    <AppTableRowCell
                                      sx={{
                                        textAlign: "center",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {idx + 1}
                                    </AppTableRowCell>
                                    <AppTableRowCell
                                      sx={{
                                        textAlign: "center",
                                        fontSize: "12px",
                                        fontWeight: 700,
                                        color: theme.palette.primary.main,
                                      }}
                                    >
                                      {mapping.order_id
                                        ? `#${mapping.order_id}`
                                        : "—"}
                                    </AppTableRowCell>
                                    <AppTableRowCell
                                      sx={{
                                        textAlign: "center",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {mapping.sku || "—"}
                                    </AppTableRowCell>
                                    <AppTableRowCell
                                      sx={{
                                        textAlign: "left",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {mapping.description || "—"}
                                    </AppTableRowCell>
                                    <AppTableRowCell
                                      sx={{
                                        textAlign: "center",
                                        fontSize: "12px",
                                        fontWeight: 700,
                                      }}
                                    >
                                      {mapping.quantity}
                                    </AppTableRowCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}

                {/* Orphan mappings */}
                {orphanMappings.length > 0 && (
                  <Card
                    sx={{
                      mb: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 3,
                      boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: "0px 8px 32px 0px rgba(0, 0, 0, 0.12)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 3,
                          pb: 2,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <CustomTypography variant="heading6" fontWeight={600}>
                          Additional Order Mappings
                        </CustomTypography>
                        <Chip
                          label={orphanMappings.length}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                      <TableContainer
                        sx={{
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow
                              sx={{
                                backgroundColor:
                                  theme.palette.customColors?.lightGreyColor,
                                "& th": { py: 1, height: 40 },
                              }}
                            >
                              <AppTableHeaderCell
                                sx={{
                                  textAlign: "center",
                                  fontSize: 12,
                                  minWidth: "50px",
                                }}
                                fontSize="12px"
                              >
                                #
                              </AppTableHeaderCell>
                              <AppTableHeaderCell
                                sx={{
                                  textAlign: "center",
                                  fontSize: 12,
                                  minWidth: "110px",
                                }}
                                fontSize="12px"
                              >
                                Product ID
                              </AppTableHeaderCell>
                              <AppTableHeaderCell
                                sx={{
                                  textAlign: "center",
                                  fontSize: 12,
                                  minWidth: "110px",
                                }}
                                fontSize="12px"
                              >
                                Order ID
                              </AppTableHeaderCell>
                              <AppTableHeaderCell
                                sx={{
                                  textAlign: "center",
                                  fontSize: 12,
                                  minWidth: "130px",
                                }}
                                fontSize="12px"
                              >
                                SKU
                              </AppTableHeaderCell>
                              <AppTableHeaderCell
                                sx={{
                                  textAlign: "left",
                                  fontSize: 12,
                                  minWidth: "200px",
                                }}
                                fontSize="12px"
                              >
                                Description
                              </AppTableHeaderCell>
                              <AppTableHeaderCell
                                sx={{
                                  textAlign: "center",
                                  fontSize: 12,
                                  minWidth: "90px",
                                }}
                                fontSize="12px"
                              >
                                Quantity
                              </AppTableHeaderCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {orphanMappings.map((mapping, idx) => (
                              <TableRow
                                key={mapping.id}
                                hover
                                sx={{
                                  "&:nth-of-type(odd)": {
                                    backgroundColor:
                                      theme.palette.background.paper,
                                  },
                                  "&:nth-of-type(even)": {
                                    backgroundColor: theme.palette.action.hover,
                                  },
                                  "& td": { fontSize: "12px" },
                                }}
                              >
                                <AppTableRowCell
                                  sx={{ textAlign: "center", fontSize: "12px" }}
                                >
                                  {idx + 1}
                                </AppTableRowCell>
                                <AppTableRowCell
                                  sx={{
                                    textAlign: "center",
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    color: theme.palette.primary.main,
                                  }}
                                >
                                  #{mapping.product_id}
                                </AppTableRowCell>
                                <AppTableRowCell
                                  sx={{
                                    textAlign: "center",
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    color: theme.palette.primary.main,
                                  }}
                                >
                                  {mapping.order_id
                                    ? `#${mapping.order_id}`
                                    : "—"}
                                </AppTableRowCell>
                                <AppTableRowCell
                                  sx={{ textAlign: "center", fontSize: "12px" }}
                                >
                                  {mapping.sku || "—"}
                                </AppTableRowCell>
                                <AppTableRowCell
                                  sx={{ textAlign: "left", fontSize: "12px" }}
                                >
                                  {mapping.description || "—"}
                                </AppTableRowCell>
                                <AppTableRowCell
                                  sx={{
                                    textAlign: "center",
                                    fontSize: "12px",
                                    fontWeight: 700,
                                  }}
                                >
                                  {mapping.quantity}
                                </AppTableRowCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </>
          )}
        </>
      )}

      {/* Approve confirmation */}
      {approvePopupOpen && (
        <ConfirmationPopup
          id={requestId}
          open={approvePopupOpen}
          title="Approve Stock Issue Request"
          content={`Are you sure you want to approve Request #${requestId}? This action cannot be undone.`}
          onConfirm={handleApproveConfirm}
          onCancel={() => setApprovePopupOpen(false)}
        />
      )}

      {/* Reject confirmation */}
      {rejectPopupOpen && (
        <ConfirmationPopup
          id={requestId}
          open={rejectPopupOpen}
          title="Reject Stock Issue Request"
          content="Please provide a reason for rejection."
          type="form"
          inputRequired
          inputLabel="Rejection Reason"
          inputType="text"
          onConfirm={handleRejectConfirm}
          onCancel={() => setRejectPopupOpen(false)}
        />
      )}
    </Grid2>
  );
};

export default observer(StockIssueRequestDetail);
